import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  AttachedFileMeta,
  EmployeePick,
  ExcelImportPreview,
  MinutesCreateRequest,
  MinutesCreateResponse,
  PagedResponse,
} from '../types'

/** Aktif toplantı gruplarını (dropdown seçenekleri) döndürür — 038 ucundan okur (salt-okuma). */
export async function getMeetingGroupOptions(): Promise<{ id: number; name: string }[]> {
  const { data } = await apiClient.get<PagedResponse<{ id: number; name: string }>>(
    '/management/meeting-groups',
    { params: { page: 0, size: 200 } },
  )
  return data.content.map((g) => ({ id: g.id, name: g.name }))
}

/** Katılımcı/atanan kişi araması (aktif + müdür ve üstü). */
export async function searchEmployees(q: string): Promise<EmployeePick[]> {
  const { data } = await apiClient.get<PagedResponse<EmployeePick>>(
    '/management/employees/search',
    {
      params: { q, page: 0, size: 20 },
    },
  )
  return data.content
}

/** Tutanağı oluşturur (Yayınla) — toplantı + katılımcılar atomik. */
/**
 * Tutanak Oluştur ekranına erişim izi (denetim kodu 500).
 *
 * <p>Yazma işlemleri logu sunucuda kendi akışlarının sonunda yazar; görüntüleme/erişim olaylarının
 * böyle bir akışı olmadığı için arayüz bunu açıkça tetikler.
 *
 * <p>Hata **yutulur**: denetim izi kullanıcının ekranı açmasını engellememelidir. Bu yüzden çağıran
 * `await` etmez ve başarısızlık sessizce geçilir (sunucu tarafı da best-effort yazar).
 */
export async function recordMinutesCreateScreenAccess(): Promise<void> {
  try {
    await apiClient.post('/management/minutes/screen-access')
  } catch {
    // Denetim izi kullanıcı akışını bozmaz.
  }
}

export async function createMinutes(body: MinutesCreateRequest): Promise<MinutesCreateResponse> {
  const { data } = await apiClient.post<MinutesCreateResponse>('/management/minutes', body)
  return data
}

/**
 * Content-Disposition başlığından indirme adını çözer (076). Önce RFC 5987 `filename*=UTF-8''<pct>`,
 * yoksa düz `filename="..."`; hiçbiri yoksa fallback. Sunucu açıklayıcı Türkçe adı burada üretir.
 */
function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback
  const star = /filename\*=([^;]+)/i.exec(header)
  if (star?.[1]) {
    let value = star[1].trim()
    const utf8 = /^UTF-8''(.*)$/i.exec(value)
    if (utf8) value = utf8[1]
    try {
      return decodeURIComponent(value.replace(/(?:^")|(?:"$)/g, '')) || fallback
    } catch {
      /* bozuk kodlama → düz filename'e / fallback'e düş */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || fallback
}

/** Blob'u verilen adla tarayıcıda indirir. */
function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Rapor Al (Toplantı Detay): toplantının O ANKİ güncel durumundan tutanak PDF'ini üretip indirir
 * (kaydetmez). İndirme adı sunucunun Content-Disposition başlığından (açıklayıcı Türkçe ad) çözülür.
 */
export async function downloadMinutesReportPdf(meetingId: number): Promise<void> {
  const res = await apiClient.get<Blob>(`/management/minutes/${meetingId}/report-pdf`, {
    responseType: 'blob',
  })
  const disposition = res.headers?.['content-disposition']
  const fileName = filenameFromContentDisposition(
    typeof disposition === 'string' ? disposition : undefined,
    `Tutanak-${meetingId}.pdf`,
  )
  triggerDownload(res.data, fileName)
}

/** Önizleme (044): girilen bilgilerden PDF'i kaydetmeden üretir → tarayıcıda indirir. */
export async function downloadMinutesPreviewPdf(body: MinutesCreateRequest): Promise<void> {
  const res = await apiClient.post<Blob>('/management/minutes/preview-pdf', body, {
    responseType: 'blob',
  })
  const disposition = res.headers?.['content-disposition']
  const fileName = filenameFromContentDisposition(
    typeof disposition === 'string' ? disposition : undefined,
    'Tutanak-onizleme.pdf',
  )
  triggerDownload(res.data, fileName)
}

/** Oluşturulan tutanağa dosya(lar) iliştirir (pending-first; her dosya bağımsız). */
export async function uploadMinutesAttachments(
  meetingId: number,
  files: File[],
): Promise<AttachedFileMeta[]> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  const { data } = await apiClient.post<AttachedFileMeta[]>(
    `/management/minutes/${meetingId}/attachments`,
    form,
    // apiClient varsayılanı application/json; FormData'da multipart'a çevrilmeli (yoksa "not a multipart
    // request" hatası). Boundary'yi axios/tarayıcı ekler. (024 uploadAttachments ile aynı desen.)
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}
// ── 088 Excel ile toplu veri yükleme ──

/**
 * Doldurulmak üzere üç sayfalı Excel şablonunu indirir. Dosya adı sunucunun
 * `Content-Disposition` başlığından okunur (ortak {@link downloadXlsx} yardımcısı); başlık dili
 * `Accept-Language` ile belirlenir.
 */
export async function downloadExcelTemplate(): Promise<void> {
  await downloadXlsx('/management/minutes/excel-template', {}, 'tutanak-excel-sablonu.xlsx')
}

/**
 * Doldurulmuş dosyayı doğrulatır ve ön izleme sonucunu döndürür — **kalıcı kayıt oluşturmaz**.
 * `meetingStartDate` verilirse hedef teslim tarihi alt sınırı `max(toplantı, bugün)` olur (088/FR-028);
 * ekranda tarih henüz girilmemişse gönderilmez.
 */
export async function previewExcelImport(
  file: File,
  meetingStartDate?: string | null,
  meetingId?: number | null,
): Promise<ExcelImportPreview> {
  const form = new FormData()
  form.append('file', file)
  if (meetingStartDate) form.append('meetingStartDate', meetingStartDate)
  // `meetingId` yalnız Toplantı Düzenle akışında gönderilir: uç durumsuz kalır (kayıt yazmaz), ama
  // denetim izi (503/504) o toplantıya bağlanır ve toplantının İşlem Geçmişi'nde görünür. Tutanak
  // Oluştur akışında toplantı henüz yoktur → gönderilmez, iz bağlamsız yazılır (093).
  if (meetingId != null) form.append('meetingId', String(meetingId))
  const { data } = await apiClient.post<ExcelImportPreview>(
    '/management/minutes/excel-preview',
    form,
  )
  return data
}
